import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  FileClock, Plus, X, Search, MessageCircle, CheckCircle2, XCircle,
  RotateCcw, PackageCheck, Clock, Eye, Pencil, PhoneCall, History,
} from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { useDebounce } from "@/hooks/useDebounce";
import { hariIniISO, formatTanggal } from "@/lib/tanggal";

const STATUS_META = {
  menunggu: { label: "Menunggu", chip: "bg-amber-100 text-amber-800", icon: Clock },
  tersedia: { label: "Tersedia — hubungi!", chip: "bg-emerald-100 text-emerald-800", icon: PackageCheck },
  selesai: { label: "Selesai", chip: "bg-sky-100 text-sky-800", icon: CheckCircle2 },
  batal: { label: "Batal", chip: "bg-slate-100 text-slate-500", icon: XCircle },
};

const TABS = [
  { key: "semua", label: "Semua" },
  { key: "menunggu", label: "Menunggu" },
  { key: "tersedia", label: "Tersedia" },
  { key: "selesai", label: "Selesai" },
  { key: "batal", label: "Batal" },
];

const hariIni = () => hariIniISO();

const emptyForm = () => ({
  tanggal: hariIni(), nama_pelanggan: "", no_hp: "",
  sku: "", nama_barang: "", cabang: "", catatan: "",
});

/** 08xx -> 628xx (wa.me hanya menerima format internasional tanpa +). */
const waNumber = (hp) => {
  const d = (hp || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("62")) return d;
  if (d.startsWith("0")) return "62" + d.slice(1);
  return "62" + d;
};

const waLink = (it) => {
  const n = waNumber(it.no_hp);
  if (!n) return null;
  const text = `Halo ${it.nama_pelanggan}, barang ${it.nama_barang} yang Anda tunggu sudah tersedia di ${it.cabang || "toko kami"}`;
  return `https://wa.me/${n}?text=${encodeURIComponent(text)}`;
};

const FOLLOWUP_TABS = [
  { key: "semua", label: "Semua Follow-up" },
  { key: "belum", label: "Belum Follow-up" },
  { key: "sudah", label: "Sudah Follow-up" },
];

/** ISO -> "06/08/2026 14:05" (atau "-" untuk entri lama tanpa jejak waktu). */
const fmtWaktu = (iso) => {
  if (!iso || iso === "-") return "-";
  return formatTanggal(iso, { denganJam: true });
};

/** Susun ulang field waktu/status entri jadi timeline kronologis untuk modal detail. */
const buildRiwayat = (it) => {
  if (!it) return [];
  const rows = [];
  if (it.created_at && it.created_at !== "-") {
    rows.push({ label: "Dicatat", by: it.created_by_name, at: it.created_at });
  }
  if (it.restock_at) {
    rows.push({ label: `Ditandai tersedia (restock ${it.restock_ref || "-"})`, by: null, at: it.restock_at });
  }
  if (it.follow_up_at) {
    rows.push({
      label: `Follow-up ditandai "${it.status_follow_up === "sudah" ? "sudah" : "belum"}"${it.follow_up_catatan ? ` — ${it.follow_up_catatan}` : ""}`,
      by: it.follow_up_by_name, at: it.follow_up_at,
    });
  }
  if (it.selesai_at) {
    rows.push({ label: "Ditandai selesai", by: it.updated_by, at: it.selesai_at });
  }
  if (it.batal_alasan) {
    rows.push({ label: `Dibatalkan — alasan: ${it.batal_alasan}`, by: it.updated_by, at: it.updated_at });
  }
  if (it.updated_at && it.updated_at !== it.selesai_at && !it.batal_alasan) {
    rows.push({ label: "Terakhir diedit", by: it.updated_by, at: it.updated_at });
  }
  return rows.sort((a, b) => new Date(a.at) - new Date(b.at));
};

export default function WaitingList() {
  const { can } = useAuth();
  // Autocomplete produk memakai endpoint milik modul inventory_produk — modul
  // berbeda dari waiting_list yang menggerbangi halaman ini. Kalau tetap
  // dipanggil tanpa izin, tiap ketikan menghasilkan 403; kasir tanpa izin
  // inventory cukup mengetik nama barang bebas.
  const bolehLookup = can("inventory_produk", "view");
  const bolehTulis = can("waiting_list", "create");
  const bolehUbah = can("waiting_list", "update");

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("semua");
  const [followUp, setFollowUp] = useState("semua");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [branches, setBranches] = useState([]);
  const [saran, setSaran] = useState([]);
  const debounceRef = useRef(null);

  // Detail (brief bos: "bisa melihat detail")
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit (brief bos: "bisa edit")
  const [editForm, setEditForm] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editSaran, setEditSaran] = useState([]);
  const editDebounceRef = useRef(null);

  const load = async (statusTab = tab, cari = q, fu = followUp) => {
    setLoading(true);
    try {
      const params = {};
      if (statusTab !== "semua") params.status = statusTab;
      if (cari.trim()) params.q = cari.trim();
      if (fu && fu !== "semua") params.follow_up = fu;
      const [a, b] = await Promise.all([
        api.get("/waiting-list", { params }),
        api.get("/waiting-list/summary"),
      ]);
      setItems(a.data.items || []);
      setSummary(b.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat waiting list");
    } finally { setLoading(false); }
  };

  const qDebounced = useDebounce(q, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(tab, qDebounced, followUp); }, [tab, followUp, qDebounced]);

  useEffect(() => {
    // Daftar cabang untuk datalist form — endpoint terbuka untuk semua user
    // login. Kalau gagal, input cabang tetap bisa diketik bebas.
    api.get("/finance/branches/master")
      .then(r => setBranches((r.data.items || []).map(b => b.name)))
      .catch(() => {});
  }, []);

  const cariProduk = (teks) => {
    setForm(f => ({ ...f, nama_barang: teks, sku: "" }));
    if (!bolehLookup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!teks || teks.trim().length < 2) { setSaran([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/inventory/products/lookup", { params: { q: teks.trim() } });
        setSaran(r.data.items || []);
      } catch { setSaran([]); }
    }, 300);
  };

  const pilihProduk = (p) => {
    setForm(f => ({ ...f, nama_barang: p.nama, sku: p.sku }));
    setSaran([]);
  };

  const simpan = async () => {
    setBusy(true);
    try {
      const r = await api.post("/waiting-list", form);
      toast.success(r.data.message || "Tercatat");
      setForm(null);
      setSaran([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setBusy(false); }
  };

  const ubahStatus = async (it, status) => {
    let alasan = "";
    if (status === "batal") {
      alasan = window.prompt(`Alasan membatalkan waiting list ${it.nama_pelanggan}?`) || "";
      if (!alasan.trim()) return;
    }
    setBusy(true);
    try {
      const r = await api.patch(`/waiting-list/${it.id}/status`, { status, alasan });
      toast.success(r.data.message || "Status diubah");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah status");
    } finally { setBusy(false); }
  };

  const bukaDetail = async (it) => {
    setDetailItem(it);
    setDetailLoading(true);
    try {
      const r = await api.get(`/waiting-list/${it.id}`);
      setDetailItem(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat detail");
    } finally { setDetailLoading(false); }
  };

  const bukaEdit = (it) => {
    setEditForm({
      id: it.id, tanggal: it.tanggal || hariIni(), nama_pelanggan: it.nama_pelanggan || "",
      no_hp: it.no_hp || "", sku: it.sku || "", nama_barang: it.nama_barang || "",
      catatan: it.catatan || "", cabang: it.cabang || "",
    });
    setEditSaran([]);
  };

  const cariProdukEdit = (teks) => {
    setEditForm(f => ({ ...f, nama_barang: teks, sku: "" }));
    if (!bolehLookup) return;
    if (editDebounceRef.current) clearTimeout(editDebounceRef.current);
    if (!teks || teks.trim().length < 2) { setEditSaran([]); return; }
    editDebounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/inventory/products/lookup", { params: { q: teks.trim() } });
        setEditSaran(r.data.items || []);
      } catch { setEditSaran([]); }
    }, 300);
  };

  const pilihProdukEdit = (p) => {
    setEditForm(f => ({ ...f, nama_barang: p.nama, sku: p.sku }));
    setEditSaran([]);
  };

  const simpanEdit = async () => {
    if (!editForm) return;
    setEditBusy(true);
    try {
      const { id, ...body } = editForm;
      const r = await api.patch(`/waiting-list/${id}`, body);
      toast.success(r.data.message || "Entri diperbarui");
      setEditForm(null);
      setEditSaran([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan perubahan");
    } finally { setEditBusy(false); }
  };

  const toggleFollowUp = async (it) => {
    const target = it.status_follow_up === "sudah" ? "belum" : "sudah";
    let catatan = "";
    if (target === "sudah") {
      const masukan = window.prompt(`Catatan follow-up untuk ${it.nama_pelanggan}? (opsional, kosongkan bila tidak ada)`);
      if (masukan === null) return; // dibatalkan
      catatan = masukan.trim();
    }
    setBusy(true);
    try {
      const r = await api.patch(`/waiting-list/${it.id}/follow-up`, { status_follow_up: target, catatan });
      toast.success(r.data.message || "Status follow-up diperbarui");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menandai follow-up");
    } finally { setBusy(false); }
  };

  const cards = [
    { key: "menunggu", label: "Menunggu Barang", tint: "text-amber-700" },
    { key: "tersedia", label: "Tersedia — Hubungi", tint: "text-emerald-700" },
    { key: "selesai", label: "Selesai", tint: "text-sky-700" },
    { key: "batal", label: "Batal", tint: "text-slate-500" },
  ];

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="waiting-list">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">POS &amp; Sales</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <FileClock className="w-7 h-7 text-emerald-600" /> Waiting List Barang
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Pelanggan yang mencari barang kosong. Saat barangnya diterima gudang,
            entri otomatis jadi <span className="font-medium text-emerald-700">tersedia</span> — segera hubungi sebelum terjual lagi.
          </p>
        </div>
        {bolehTulis && (
          <button onClick={() => setForm(emptyForm())} data-testid="wl-add"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Catat Pelanggan
          </button>
        )}
      </div>

      {/* Kartu ringkasan per status */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => (
          <div key={c.key}
               className={`bg-white border rounded-xl p-4 shadow-sm ${c.key === "tersedia" && (summary.tersedia || 0) > 0 ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
            <p className={`font-display text-2xl font-bold mt-1.5 ${c.tint}`} data-testid={`wl-count-${c.key}`}>
              {summary[c.key] ?? 0}
            </p>
          </div>
        ))}
      </div>

      {/* Tab status + filter follow-up + pencarian */}
      <div className="flex items-center justify-between gap-3 flex-col md:flex-row">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 flex-wrap">
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`wl-tab-${t.key}`}
                      className={`h-8 px-3 rounded-md text-sm font-medium ${tab === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
                {t.label}{t.key !== "semua" && ` (${summary[t.key] ?? 0})`}
              </button>
            ))}
          </div>
          <select value={followUp} onChange={e => setFollowUp(e.target.value)} data-testid="wl-filter-followup"
                  className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500">
            {FOLLOWUP_TABS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>
        <form className="flex items-center gap-2 w-full md:w-auto"
              onSubmit={(e) => { e.preventDefault(); load(tab, q); }}>
          <div className="relative flex-1 md:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)} data-testid="wl-search"
                   placeholder="Cari nama / barang / HP..."
                   className="h-9 w-full pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <button type="submit" data-testid="wl-search-btn"
                  className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700">
            Cari
          </button>
        </form>
      </div>

      {/* Daftar */}
      <div className="space-y-2">
        <LoadingState
          loading={loading}
          kosong={!loading && items.length === 0}
          pesanKosong={`Belum ada entri${tab !== "semua" ? ` berstatus "${tab}"` : ""}.`}
          data-testid="wl-loading"
        >
        {!loading && items.map(it => {
          const meta = STATUS_META[it.status] || STATUS_META.menunggu;
          const Icon = meta.icon;
          const wa = waLink(it);
          const tersedia = it.status === "tersedia";
          return (
            <div key={it.id} data-testid={`wl-row-${it.id}`}
                 className={`bg-white border rounded-xl p-4 shadow-sm ${tersedia ? "border-emerald-400 ring-1 ring-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${meta.chip}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${it.status_follow_up === "sudah" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                      <PhoneCall className="w-3 h-3" /> {it.status_follow_up === "sudah" ? "Sudah follow-up" : "Belum follow-up"}
                    </span>
                    <span className="font-mono text-[11px] text-slate-400">{it.code}</span>
                    <span className="text-[11px] text-slate-400">{it.tanggal}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900 mt-1.5">
                    {it.nama_pelanggan}
                    {it.no_hp && <span className="font-normal text-slate-500"> · {it.no_hp}</span>}
                  </p>
                  <p className="text-sm text-slate-700 mt-0.5">
                    {it.nama_barang}
                    {it.sku && <span className="font-mono text-[11px] text-slate-400 ml-1.5">({it.sku})</span>}
                    {it.cabang && <span className="text-xs text-slate-500"> — {it.cabang}</span>}
                  </p>
                  {tersedia && it.restock_ref && (
                    <p className="text-xs text-emerald-700 mt-1 font-medium">
                      Barang masuk lewat {it.restock_ref}
                      {it.restock_at && ` · ${String(it.restock_at).slice(0, 10)}`}
                    </p>
                  )}
                  {it.status === "batal" && it.batal_alasan && (
                    <p className="text-xs text-rose-600 mt-1">Alasan batal: {it.batal_alasan}</p>
                  )}
                  {it.catatan && <p className="text-xs text-slate-500 mt-1">Catatan: {it.catatan}</p>}
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    Diinput oleh <span className="font-medium text-slate-600">{it.created_by_name || it.created_by || "-"}</span>
                    {it.created_at && it.created_at !== "-" && <> · {fmtWaktu(it.created_at)}</>}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => bukaDetail(it)} data-testid={`wl-detail-${it.id}`}
                          className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
                    <Eye className="w-4 h-4" /> Detail
                  </button>
                  {bolehUbah && (
                    <button onClick={() => bukaEdit(it)} data-testid={`wl-edit-${it.id}`}
                            className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
                      <Pencil className="w-4 h-4" /> Edit
                    </button>
                  )}
                  {bolehUbah && (
                    <button onClick={() => toggleFollowUp(it)} disabled={busy} data-testid={`wl-followup-${it.id}`}
                            className={`h-9 px-3 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50 ${it.status_follow_up === "sudah" ? "border border-slate-200 text-slate-500 hover:bg-slate-50" : "border border-amber-300 text-amber-700 hover:bg-amber-50"}`}>
                      <PhoneCall className="w-4 h-4" /> {it.status_follow_up === "sudah" ? "Buka Lagi Follow-up" : "Tandai Sudah Follow-up"}
                    </button>
                  )}
                  {wa && (it.status === "tersedia" || it.status === "menunggu") && (
                    <a href={wa} target="_blank" rel="noreferrer" data-testid={`wl-wa-${it.id}`}
                       className={`h-9 px-3 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 ${tersedia ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                  )}
                  {bolehUbah && (it.status === "menunggu" || it.status === "tersedia") && (
                    <>
                      <button onClick={() => ubahStatus(it, "selesai")} disabled={busy}
                              data-testid={`wl-done-${it.id}`}
                              className="h-9 px-3 rounded-lg border border-sky-300 text-sky-700 hover:bg-sky-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                        <CheckCircle2 className="w-4 h-4" /> Selesai
                      </button>
                      <button onClick={() => ubahStatus(it, "batal")} disabled={busy}
                              data-testid={`wl-batal-${it.id}`}
                              className="h-9 px-3 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                        <XCircle className="w-4 h-4" /> Batal
                      </button>
                    </>
                  )}
                  {bolehUbah && (it.status === "selesai" || it.status === "batal") && (
                    <button onClick={() => ubahStatus(it, "menunggu")} disabled={busy}
                            data-testid={`wl-reopen-${it.id}`}
                            className="h-9 px-3 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                      <RotateCcw className="w-4 h-4" /> Buka Lagi
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        </LoadingState>
      </div>

      {/* Form tambah */}
      {form && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={() => { setForm(null); setSaran([]); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900">Catat Pelanggan Menunggu Barang</p>
              <button onClick={() => { setForm(null); setSaran([]); }} data-testid="wl-cancel"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Tanggal</label>
                  <DatePicker value={form.tanggal} data-testid="wl-f-tanggal"
                         onChange={v => setForm(f => ({ ...f, tanggal: v }))}
                         className="mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Cabang</label>
                  <input value={form.cabang} list="wl-branches" data-testid="wl-f-cabang"
                         placeholder="Toko tempat pelanggan datang"
                         onChange={e => setForm(f => ({ ...f, cabang: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  <datalist id="wl-branches">
                    {branches.map(b => <option key={b} value={b} />)}
                  </datalist>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Nama Pelanggan <span className="text-rose-500">*</span></label>
                <input value={form.nama_pelanggan} data-testid="wl-f-nama"
                       placeholder="cth. Budi Santoso"
                       onChange={e => setForm(f => ({ ...f, nama_pelanggan: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">No. HP / WhatsApp</label>
                <input value={form.no_hp} data-testid="wl-f-hp" inputMode="tel"
                       placeholder="08xxxxxxxxxx"
                       onChange={e => setForm(f => ({ ...f, no_hp: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div className="relative">
                <label className="text-xs font-medium text-slate-600">Barang yang Dicari <span className="text-rose-500">*</span></label>
                <input value={form.nama_barang} data-testid="wl-f-barang"
                       placeholder={bolehLookup ? "Ketik untuk cari di master, atau tulis bebas" : "cth. iPhone 15 Pro 256GB Hitam"}
                       onChange={e => cariProduk(e.target.value)}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                {form.sku && (
                  <p className="text-[11px] text-emerald-700 mt-1">
                    Terhubung ke SKU <span className="font-mono">{form.sku}</span> — pencocokan restock akan persis.
                  </p>
                )}
                {saran.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    {saran.map(p => (
                      <button key={p.sku} type="button" onClick={() => pilihProduk(p)}
                              data-testid={`wl-saran-${p.sku}`}
                              className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm border-b border-slate-50 last:border-0">
                        <span className="font-medium text-slate-900">{p.nama}</span>
                        <span className="font-mono text-[11px] text-slate-400 ml-1.5">{p.sku}</span>
                        <span className={`text-[11px] ml-1.5 ${p.stock_qty > 0 ? "text-emerald-600" : "text-rose-500"}`}>
                          stok {p.stock_qty ?? 0}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Catatan</label>
                <textarea value={form.catatan} rows={2} data-testid="wl-f-catatan"
                          placeholder="cth. warna apa saja asal 256GB, siap DP"
                          onChange={e => setForm(f => ({ ...f, catatan: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={() => { setForm(null); setSaran([]); }}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Batal
              </button>
              <button onClick={simpan} disabled={busy || !form.nama_pelanggan.trim() || !form.nama_barang.trim()}
                      data-testid="wl-save"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal detail — semua field + riwayat */}
      {detailItem && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={() => setDetailItem(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="wl-detail-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900">Detail Waiting List</p>
              <button onClick={() => setDetailItem(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {detailLoading ? (
                <LoadingState loading jumlahKartu={1} data-testid="wl-detail-loading" />
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${(STATUS_META[detailItem.status] || STATUS_META.menunggu).chip}`}>
                      {(STATUS_META[detailItem.status] || STATUS_META.menunggu).label}
                    </span>
                    <span className="font-mono text-[11px] text-slate-400">{detailItem.code}</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-sm">
                    <div><dt className="text-[11px] text-slate-400">Tanggal</dt><dd className="text-slate-800">{detailItem.tanggal || "-"}</dd></div>
                    <div><dt className="text-[11px] text-slate-400">Cabang</dt><dd className="text-slate-800">{detailItem.cabang || "-"}</dd></div>
                    <div><dt className="text-[11px] text-slate-400">Nama Pelanggan</dt><dd className="text-slate-800 font-medium">{detailItem.nama_pelanggan || "-"}</dd></div>
                    <div><dt className="text-[11px] text-slate-400">No. HP</dt><dd className="text-slate-800">{detailItem.no_hp || "-"}</dd></div>
                    <div className="col-span-2"><dt className="text-[11px] text-slate-400">Barang yang Dicari</dt>
                      <dd className="text-slate-800">{detailItem.nama_barang || "-"}{detailItem.sku && <span className="font-mono text-[11px] text-slate-400 ml-1.5">({detailItem.sku})</span>}</dd></div>
                    {detailItem.catatan && (<div className="col-span-2"><dt className="text-[11px] text-slate-400">Catatan</dt><dd className="text-slate-800">{detailItem.catatan}</dd></div>)}
                    {detailItem.status === "batal" && detailItem.batal_alasan && (
                      <div className="col-span-2"><dt className="text-[11px] text-slate-400">Alasan Batal</dt><dd className="text-rose-600">{detailItem.batal_alasan}</dd></div>
                    )}
                    <div className="col-span-2 border-t border-slate-100 pt-2.5">
                      <dt className="text-[11px] text-slate-400">Diinput oleh</dt>
                      <dd className="text-slate-800">{detailItem.created_by_name || detailItem.created_by || "-"} · {fmtWaktu(detailItem.created_at)}</dd>
                    </div>
                  </dl>
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 inline-flex items-center gap-1.5">
                      <History className="w-3.5 h-3.5" /> Riwayat
                    </p>
                    <ul className="space-y-1.5 text-xs text-slate-600">
                      {buildRiwayat(detailItem).map((r, i) => (
                        <li key={i}>• {r.label}{r.by ? ` — ${r.by}` : ""} · {fmtWaktu(r.at)}</li>
                      ))}
                      {buildRiwayat(detailItem).length === 0 && (
                        <li className="text-slate-400">Belum ada riwayat perubahan.</li>
                      )}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal edit */}
      {editForm && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={() => { setEditForm(null); setEditSaran([]); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="wl-edit-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900">Edit Waiting List</p>
              <button onClick={() => { setEditForm(null); setEditSaran([]); }} data-testid="wl-edit-cancel"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Tanggal</label>
                  <DatePicker value={editForm.tanggal} data-testid="wl-ef-tanggal"
                         onChange={v => setEditForm(f => ({ ...f, tanggal: v }))}
                         className="mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Cabang</label>
                  <input value={editForm.cabang} list="wl-branches" data-testid="wl-ef-cabang"
                         placeholder="Toko tempat pelanggan datang"
                         onChange={e => setEditForm(f => ({ ...f, cabang: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Nama Pelanggan <span className="text-rose-500">*</span></label>
                <input value={editForm.nama_pelanggan} data-testid="wl-ef-nama"
                       onChange={e => setEditForm(f => ({ ...f, nama_pelanggan: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">No. HP / WhatsApp</label>
                <input value={editForm.no_hp} data-testid="wl-ef-hp" inputMode="tel"
                       onChange={e => setEditForm(f => ({ ...f, no_hp: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div className="relative">
                <label className="text-xs font-medium text-slate-600">Barang yang Dicari <span className="text-rose-500">*</span></label>
                <input value={editForm.nama_barang} data-testid="wl-ef-barang"
                       placeholder={bolehLookup ? "Ketik untuk cari di master, atau tulis bebas" : "cth. iPhone 15 Pro 256GB Hitam"}
                       onChange={e => cariProdukEdit(e.target.value)}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                {editForm.sku && (
                  <p className="text-[11px] text-emerald-700 mt-1">
                    Terhubung ke SKU <span className="font-mono">{editForm.sku}</span> — pencocokan restock akan persis.
                  </p>
                )}
                {editSaran.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    {editSaran.map(p => (
                      <button key={p.sku} type="button" onClick={() => pilihProdukEdit(p)}
                              data-testid={`wl-ef-saran-${p.sku}`}
                              className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm border-b border-slate-50 last:border-0">
                        <span className="font-medium text-slate-900">{p.nama}</span>
                        <span className="font-mono text-[11px] text-slate-400 ml-1.5">{p.sku}</span>
                        <span className={`text-[11px] ml-1.5 ${p.stock_qty > 0 ? "text-emerald-600" : "text-rose-500"}`}>
                          stok {p.stock_qty ?? 0}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Catatan</label>
                <textarea value={editForm.catatan} rows={2} data-testid="wl-ef-catatan"
                          onChange={e => setEditForm(f => ({ ...f, catatan: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={() => { setEditForm(null); setEditSaran([]); }}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Batal
              </button>
              <button onClick={simpanEdit} disabled={editBusy || !editForm.nama_pelanggan.trim() || !editForm.nama_barang.trim()}
                      data-testid="wl-ef-save"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {editBusy ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
